import React, { useEffect, useMemo, useRef, useState } from "react";
import { Clock, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Generate all HH:MM slots in stepMin increments (default 15min) → ["00:00","00:15",...]
function buildSlots(stepMin = 15) {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMin) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
}

// Picks the closest slot index for a given HH:MM (used to scroll into view)
function nearestIndex(slots, value) {
  if (!value) return -1;
  const direct = slots.indexOf(value);
  if (direct >= 0) return direct;
  const [h, m] = value.split(":").map((x) => parseInt(x, 10));
  if (isNaN(h) || isNaN(m)) return -1;
  const target = h * 60 + m;
  let best = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < slots.length; i++) {
    const [sh, sm] = slots[i].split(":").map((x) => parseInt(x, 10));
    const diff = Math.abs(sh * 60 + sm - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  }
  return best;
}

/**
 * Custom Time Picker with 15-min predefined slots.
 * - value/onChange use HH:MM strings (backend-compatible)
 * - Click trigger → popover with scrollable list of slots
 * - Highlights current value, scrolls to it on open
 */
export function TimePicker({ value = "", onChange, stepMin = 15, placeholder = "—:—", testid }) {
  const slots = useMemo(() => buildSlots(stepMin), [stepMin]);
  const [open, setOpen] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Defer to ensure list is rendered
    const t = setTimeout(() => {
      const idx = nearestIndex(slots, value);
      if (idx >= 0 && listRef.current) {
        const el = listRef.current.querySelector(`[data-slot-index="${idx}"]`);
        if (el) el.scrollIntoView({ block: "center" });
      }
    }, 30);
    return () => clearTimeout(t);
  }, [open, slots, value]);

  const handlePick = (slot) => {
    onChange?.(slot);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mt-1 w-full flex items-center justify-between rounded-sm border border-input bg-white h-10 px-3 text-sm transition-colors hover:border-primary focus:outline-none focus:ring-1 focus:ring-primary data-[state=open]:border-primary data-[state=open]:ring-1 data-[state=open]:ring-primary"
          data-testid={testid}
        >
          <span className={`flex items-center gap-2 ${value ? "text-neutral-900" : "text-neutral-400"}`}>
            <Clock className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />
            <span className="font-medium tabular-nums">{value || placeholder}</span>
          </span>
          {value && (
            <span
              role="button"
              tabIndex={-1}
              onClick={handleClear}
              className="text-neutral-300 hover:text-neutral-600 transition-colors"
              data-testid={testid ? `${testid}-clear` : undefined}
              aria-label="Cancella orario"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.5} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-44 p-0 rounded-sm"
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <div
          ref={listRef}
          className="max-h-64 overflow-y-auto py-1"
          role="listbox"
        >
          {slots.map((slot, i) => {
            const selected = slot === value;
            return (
              <button
                key={slot}
                type="button"
                data-slot-index={i}
                onClick={() => handlePick(slot)}
                className={`w-full text-left px-3 py-1.5 text-sm tabular-nums transition-colors ${
                  selected
                    ? "bg-primary text-white font-medium"
                    : "text-neutral-700 hover:bg-secondary/60"
                }`}
                data-testid={testid ? `${testid}-slot-${slot}` : undefined}
                role="option"
                aria-selected={selected}
              >
                {slot}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Drop-in replacement for the inline `<Field type="time" />` pattern used in pages.
export function TimePickerField({ label, value, onChange, testid, className = "" }) {
  return (
    <div className={className}>
      <Label className="label-uppercase">{label}</Label>
      <TimePicker value={value} onChange={onChange} testid={testid} />
    </div>
  );
}
